// Core
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

// Chakra
import {
  Text,
  useColorModeValue,
  Divider,
  Button,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  useDisclosure,
  Switch,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import Attention from '../../../components/Modals/Attention';
import { DeleteIcon } from '@chakra-ui/icons';
import { priceToCents } from '../../../services/Utils';
import usePermissionStatusByKey from '../../../hooks/usePermissionStatusByKey';

const MarketPrice = () => {
  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [marketPrices, setMarketPrices] = useState([]);
  const [marketPricesMeta, setMarketPricesMeta] = useState([]);
  const limit = 25;

  const isMarketPrices = usePermissionStatusByKey('GET:/admin/market-prices');
  const isCreateMarketPrice = usePermissionStatusByKey(
    'POST:/admin/market-prices',
  );
  const isUpdateMarketPrice = usePermissionStatusByKey(
    'PATCH:/admin/market-prices/:id',
  );
  const isDeleteMarketPrice = usePermissionStatusByKey(
    'DELETE:/admin/market-prices/:id',
  );

  const { isFetching, refetch, isLoading: isMarketPricesLoading } = useQuery({
    queryKey: ['market-prices', offset, isMarketPrices],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (isMarketPrices) {
        const params = {
          limit,
          offset,
        };

        const response = await apiService.getMarketPrices(params);
        setMarketPrices(response.data);
        setMarketPricesMeta(response.meta);
      }

      return true;
    },
    enabled: !!isMarketPrices,
    retry: false,
    refetchInterval: 30000,
  });

  const onNewButtonClicked = useCallback(() => {
    history.push('/market-prices/new');
  }, [history]);

  const handleEditOpen = useCallback(
    (plan) => {
      history.push(`/market-prices/${plan.id}`);
    },
    [history],
  );

  const [selectedMarkedPriceId, setSelectedMarkedPriceId] = useState();

  const handleDelete = useCallback(
    async (markedPriceId) => {
      await apiService.deleteMarketPrice({ id: markedPriceId });
      refetch();
    },
    [refetch],
  );

  const { isLoading, mutate } = useMutation({
    mutationFn: (markedPrice) =>
      apiService.updateMarketPrice({
        id: markedPrice.id,
        isActive: !markedPrice.isActive,
      }),
    onSuccess: () => {
      if (refetch) {
        refetch();
      }
    },
  });

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text
          fontSize="40px"
          fontStyle="normal"
          fontWeight="700"
          color={textColor}
          mb="0px"
          className="page-name"
        >
          Market Prices
        </Text>
        {isCreateMarketPrice && (
          <Button
            variant="submit-ms"
            maxW="140px"
            w="100%"
            onClick={onNewButtonClicked}
            leftIcon={<FaPlus />}
          >
            Create
          </Button>
        )}
      </Flex>

      <Divider
        opacity="1"
        mr="2px"
        borderColor={borderColor}
        borderTopWidth="1px"
        borderBottomWidth="0"
        mb="32px"
      />

      {isMarketPrices && (
        <>
          {isMarketPricesLoading ? (
            <TableSkeleton rowCount={5} />
          ) : marketPrices.length > 0 ? (
            <Card padding={'8px'}>
              <Table color={textColor}>
                <Thead>
                  <Tr>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Name
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Code
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Price
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Active
                      </Text>
                    </Td>
                    {isDeleteMarketPrice && (
                      <Td>
                        <Text variant="title-md" p="0 0 6px 0"></Text>
                      </Td>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {marketPrices.map((row) => {
                    return (
                      <Tr
                        _last={{
                          '& td': {
                            borderBottom: 'none !important',
                          },
                        }}
                        key={row.id}
                        border="unset"
                        className="cursor-tr"
                      >
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {row?.name}
                          </Text>
                        </Td>
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {row?.code}
                          </Text>
                        </Td>
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {priceToCents(row.price || 0)}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Switch
                            disabled={isLoading || isUpdateMarketPrice}
                            isChecked={row?.isActive}
                            colorscheme="teal"
                            onChange={(e) => {
                              mutate(row);
                            }}
                          />
                        </Td>
                        {isDeleteMarketPrice && (
                          <Td
                            onClick={() => handleEditOpen(row)}
                            verticalAlign="center"
                            p={paddingTd}
                          >
                            <DeleteIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMarkedPriceId(row.id);
                              }}
                              color="#DE3730"
                              fontSize="24px"
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Card>
          ) : (
            <NotFound text="Not Found" />
          )}

          <Pagination
            meta={marketPricesMeta ?? { limit, offset, total: 0 }}
            isLoadingPage={isFetching}
            currentPage={Math.floor(offset / limit) + 1}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </>
      )}

      <Attention
        variant="remove"
        handle={() => handleDelete(selectedMarkedPriceId)}
        isOpen={!!selectedMarkedPriceId}
        onClose={() => setSelectedMarkedPriceId(null)}
      />
    </>
  );
};

export default MarketPrice;
